import { MediaProps } from '@sola/schema/lib/schema.ts';

export const getAspectHeight = (aspectRatio: string) => {
  switch (aspectRatio) {
    case 'vertical':
    case '9-16':
      return 1600;
    case 'portrait':
    case '3-4':
      return 4000;
    case 'square':
    case '1':
      return 1000;
    case 'landscape':
    case '4-3':
      return 3000;
    case 'cinema':
    case '16-9':
      return 900;
    case 'auto':
    default:
      // default to a 1:1 aspect ratio
      return 1000;
  }
};

export const getAspectWidth = (aspectRatio: string) => {
  switch (aspectRatio) {
    case 'vertical':
    case '9-16':
      return 900;
    case 'portrait':
    case '3-4':
      return 3000;
    case 'square':
    case '1':
      return 1000;
    case 'landscape':
    case '4-3':
      return 4000;
    case 'cinema':
    case '16-9':
      return 1600;
    case 'auto':
    default:
      // default to a 1:1 aspect ratio
      return 1000;
  }
};

export const getMaxImageWidth = (
  aspectRatio: string,
  maximumHeight: string,
) => {
  const currentHeight = maximumHeight ? `${maximumHeight}` : `100vh`;

  switch (aspectRatio) {
    case 'vertical':
    case '9-16':
      return `min(calc(${currentHeight} * .5625), 100vw)`;
    case 'portrait':
    case '3-4':
      return `min(calc(${currentHeight} * .75), 100vw)`;
    case 'square':
    case '1':
      return `min(calc(${currentHeight} * 1), 100vw)`;
    case 'landscape':
    case '4-3':
      return `min(calc(${currentHeight} * 1.3333), 100vw)`;
    case 'cinema':
    case '16-9':
      return `min(calc(${currentHeight} * 1.7778), 100vw)`;
    case 'auto':
    default:
      return `100vw`;
  }
};

export const getMaxImageHeight = (
  aspectRatio: string,
  maximumHeight: string,
) => {
  switch (aspectRatio) {
    case 'vertical':
    case '9-16':
      return maximumHeight
        ? `min(100vh, ${maximumHeight}, calc(100vw /.5625))`
        : 'min(100vh, calc(100vw /.5625))';
    case 'portrait':
    case '3-4':
      return maximumHeight
        ? `min(100vh, ${maximumHeight}, calc(100vw / .75))`
        : 'min(100vh, calc(100vw / .75))';
    case 'square':
    case '1':
      return maximumHeight
        ? `min(100vh, 100vw, ${maximumHeight})`
        : 'min(100vh, 100vw)';
    case 'landscape':
    case '4-3':
      return maximumHeight
        ? `min(${maximumHeight}, 100vh, calc(100vw / 1.3333))`
        : 'min(100vh, calc(100vw / 1.3333))';
    case 'cinema':
    case '16-9':
      return maximumHeight
        ? `min(${maximumHeight}, 100vh, calc(100vw / 1.7778))`
        : 'min(100vh, calc(100vw / 1.7778))';
    case 'auto':
    default:
      return maximumHeight ? `min(100vh, ${maximumHeight})` : '100vh';
  }
};
export const transformFocalPointProfound = (
  focus: { x: string; y: string } | undefined,
) => {
  const newX = Math.max(0, parseFloat(focus?.x ?? '50') - 25);
  return {
    x: `${newX}%`,
    y: focus?.y ?? '50%',
  };
};

export const addXFocal = (media: MediaProps | undefined) => {
  const newMedia = {
    ...media,
    image: {
      ...media?.image,
      url: media?.image?.url ?? '',
      focus: transformFocalPointProfound(media?.image?.focus),
    },
  };
  return newMedia;
};

export const getImageVariant = (objectPosition: string, rounded: string) => {
  const focusClass = objectPosition
    ? `object-position-${objectPosition}`
    : 'object-position-initial';
  const roundedClass = rounded ? `rounded-${rounded}` : `rounded-none`;
  return `Image ${focusClass} ${roundedClass}`;
};

// @TODO: investigate making storybook with next and vite work
export const getIsOptimized = (host: string) => {
  if (host === 'localhost' || host.startsWith('ark.')) {
    return false;
  }

  return true;
};

/**
 * Generates a Next.js optimized image URL for use in schema.org structured data.
 * This creates an absolute URL that points to Next.js's image optimization endpoint.
 *
 * @param imageUrl - The source image URL (e.g., from Contentful)
 * @param width - The desired width in pixels (default: 1200)
 * @param quality - The image quality 1-100 (default: 75)
 * @returns Absolute URL to the optimized image, or original URL if on unoptimized host
 */
export const getOptimizedImageUrl = (
  imageUrl?: string | null,
  width = 1200,
  quality = 75,
): string | undefined => {
  if (!imageUrl || imageUrl.trim() === '') {
    return undefined;
  }

  const isOptimized =
    typeof window !== 'undefined'
      ? getIsOptimized(window.location.hostname)
      : true;

  if (!isOptimized) {
    return imageUrl;
  }

  const baseUrl =
    typeof window !== 'undefined'
      ? `${window.location.protocol}//${window.location.host}`
      : process.env.NEXT_PUBLIC_BASE_URL || '';

  const optimizedPath = `/_next/image?url=${encodeURIComponent(imageUrl)}&w=${width}&q=${quality}`;

  return `${baseUrl}${optimizedPath}`;
};
